'use client';

import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/utils';

const TrackedChannelsWidget = ({ onCompetitorClick }) => {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/competitors`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setChannels(data);
        } else {
          console.error("Failed to fetch tracked channels");
        }
      } catch (error) {
        console.error("Error fetching tracked channels:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChannels();
  }, []); // Runs once on mount

  // This effect checks if the list is scrollable to show the fade effect
  useEffect(() => {
    const checkOverflow = () => {
      const current = listRef.current;
      if (current) {
        setIsOverflowing(current.scrollHeight > current.clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [channels, isLoading]); // Rerun when channels are loaded

  return (
    // This component is designed to be placed inside the "overview-panel"
    <div className="text-white h-full flex flex-col">
      <h3 className="font-bold text-lg mb-4">
        Tracked Channels
        {!isLoading && channels.length > 0 && (
          <span className="text-gray-400 font-normal ml-2">({channels.length})</span>
        )}
      </h3>
      
      {isLoading ? (
        <div className="flex items-center justify-center flex-grow">
          <p className="text-gray-400 text-sm">Loading channels...</p>
        </div>
      ) : channels.length === 0 ? (
        <div className="flex items-center justify-center flex-grow text-center p-4">
          <p className="text-gray-400 text-sm">
            You haven't tracked any channels yet. Analyze a channel to add it to your list!
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* By setting a max-height, we ensure this widget doesn't grow uncontrollably
          and force other widgets in the grid to have awkward empty space. */}
          <div
            ref={listRef}
            className="custom-scrollbar flex flex-col gap-2 overflow-y-auto pr-2 max-h-[140px]"
          >
            {channels.map(channel => (
              <div
                key={channel.id}
                onClick={() => onCompetitorClick(channel)}
                className="flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-white/10"
              >
                <img
                  src={channel.thumbnail_url}
                  alt={channel.title}
                  className="w-10 h-10 rounded-full flex-shrink-0"
                />
                <p className="font-medium text-sm truncate">{channel.title}</p>
              </div>
            ))}
          </div>
          {isOverflowing && (
            <div 
              className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
              style={{ background: 'linear-gradient(to top, rgba(30, 33, 40, 0.9), transparent)' }}
            ></div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrackedChannelsWidget;